'use client';

import { useMemo, useReducer } from 'react';
import { useRouter } from 'next/navigation';
import { InfoIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SettingsTabProps } from '@/types/settings';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';
import { isOwner } from '@/lib/rbac';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import {
  useSlackData,
  useDmNotificationStatus,
  useUpdateDmNotificationStatus,
} from '@/hooks/useSlackData';
import updateChannelSubscriptions from '@/apis/updateChannelSubscriptions';
import { useComposioInstaller } from '@/hooks/useComposioInstaller';
import addBotToChannel from '@/apis/addBotToChannel';
import disconnectSlack from '@/apis/disconnectSlack';
import SlackChannelConfigModal from '../modals/SlackChannelConfigModal';
import SlackAddChannelModal from '../modals/SlackAddChannelModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import { SettingsHeader } from '../components/SettingsHeader';
import SlackWorkspaceConnection from '@/components/Settings/SlackWorkspaceConnection';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SlackChannelInfo = { id: string; name: string };
type SelectedChannelInfo = {
  id: string;
  name: string;
  notifications: string[];
};

type SlackTabState = {
  isConnecting: boolean;
  configModalOpen: boolean;
  addChannelModalOpen: boolean;
  disconnectModalOpen: boolean;
  removeNotificationsModalOpen: boolean;
  channelToRemove: SlackChannelInfo | null;
  selectedChannel: SelectedChannelInfo | null;
};

type SlackTabAction =
  | { type: 'SET_CONNECTING'; payload: boolean }
  | { type: 'SET_CONFIG_MODAL'; payload: boolean }
  | { type: 'SET_ADD_CHANNEL_MODAL'; payload: boolean }
  | { type: 'SET_DISCONNECT_MODAL'; payload: boolean }
  | { type: 'SET_REMOVE_NOTIFICATIONS_MODAL'; payload: boolean }
  | { type: 'SET_CHANNEL_TO_REMOVE'; payload: SlackChannelInfo | null }
  | { type: 'SET_SELECTED_CHANNEL'; payload: SelectedChannelInfo | null }
  | { type: 'OPEN_CONFIG'; payload: SelectedChannelInfo }
  | { type: 'CLOSE_CONFIG' }
  | { type: 'OPEN_REMOVE_NOTIFICATIONS'; payload: SlackChannelInfo }
  | { type: 'CLOSE_REMOVE_NOTIFICATIONS' };

function slackTabReducer(
  state: SlackTabState,
  action: SlackTabAction
): SlackTabState {
  switch (action.type) {
    case 'SET_CONNECTING':
      return { ...state, isConnecting: action.payload };
    case 'SET_CONFIG_MODAL':
      return { ...state, configModalOpen: action.payload };
    case 'SET_ADD_CHANNEL_MODAL':
      return { ...state, addChannelModalOpen: action.payload };
    case 'SET_DISCONNECT_MODAL':
      return { ...state, disconnectModalOpen: action.payload };
    case 'SET_REMOVE_NOTIFICATIONS_MODAL':
      return { ...state, removeNotificationsModalOpen: action.payload };
    case 'SET_CHANNEL_TO_REMOVE':
      return { ...state, channelToRemove: action.payload };
    case 'SET_SELECTED_CHANNEL':
      return { ...state, selectedChannel: action.payload };
    case 'OPEN_CONFIG':
      return {
        ...state,
        selectedChannel: action.payload,
        configModalOpen: true,
      };
    case 'CLOSE_CONFIG':
      return { ...state, configModalOpen: false, selectedChannel: null };
    case 'OPEN_REMOVE_NOTIFICATIONS':
      return {
        ...state,
        channelToRemove: action.payload,
        removeNotificationsModalOpen: true,
      };
    case 'CLOSE_REMOVE_NOTIFICATIONS':
      return {
        ...state,
        removeNotificationsModalOpen: false,
        channelToRemove: null,
      };
    default:
      return state;
  }
}

const initialSlackTabState: SlackTabState = {
  isConnecting: false,
  configModalOpen: false,
  addChannelModalOpen: false,
  disconnectModalOpen: false,
  removeNotificationsModalOpen: false,
  channelToRemove: null,
  selectedChannel: null,
};

export default function SlackTab({ className }: SettingsTabProps) {
  const router = useRouter();
  const [state, dispatch] = useReducer(slackTabReducer, initialSlackTabState);
  const {
    isConnecting,
    configModalOpen,
    addChannelModalOpen,
    disconnectModalOpen,
    removeNotificationsModalOpen,
    channelToRemove,
    selectedChannel,
  } = state;

  // Get org and user data
  const organization = useOrganizationStore.getState().organization;
  const user = useUserStore.getState().user;
  const isAdmin = isOwner(user?.Role);

  const headerObj = useMemo(
    () => ({
      ...emptyHeaderObj,
      Useruuid: user?.UserUUID,
      Useremail: user?.Email,
      Username: user?.FirstName,
      Orgname: organization?.OrgName,
      Orguuid: organization?.OrgUUID,
    }),
    [user, organization]
  );

  // Fetch Slack data using React Query
  const { data, isLoading, error, configured, channels } = useSlackData(
    organization?.OrgUUID
  );

  // Fetch DM notification status using React Query
  const dmStatusQuery = useDmNotificationStatus(
    organization?.OrgUUID,
    data.isConfigured
  );

  // Mutation for updating DM notification status
  const updateDmStatusMutation = useUpdateDmNotificationStatus(
    organization?.OrgUUID
  );

  const handleToggleDmNotifications = async (value: string) => {
    const newValue = value === 'dm';
    updateDmStatusMutation.mutate(newValue);
  };

  // Map notification types to display names
  const getNotificationName = (type: string): string => {
    const notificationMap: { [key: string]: string } = {
      PR: 'PR summaries',
      Sprint: 'Sprint reports',
      Insights: 'Team insights',
      Leaderboard: 'Leaderboard updates',
      Security: 'Security PR notifications',
      EKU: 'Daily incident digest',
    };
    return notificationMap[type] || type;
  };

  // Map backend notification types to frontend notification IDs
  const mapBackendToFrontendNotifications = (
    backendNotifications: string[]
  ): string[] => {
    const mapping: { [key: string]: string[] } = {
      PR: ['subscribe'],
      Sprint: ['sprint'],
      Insights: ['insights'],
      Leaderboard: ['leaderboard'],
      Security: ['security'],
      EKU: ['eku'],
    };

    const frontendNotifications: string[] = [];
    backendNotifications.forEach((backendNotif) => {
      if (mapping[backendNotif]) {
        frontendNotifications.push(...mapping[backendNotif]);
      }
    });

    return frontendNotifications;
  };

  const commands = [
    {
      command: '/subscribe',
      description: 'Enable PR summary notifications',
    },
    {
      command: '/sprint',
      description: 'Enable sprint report notifications',
    },
    {
      command: '/leaderboard',
      description: 'Enable leaderboard updates',
    },
    {
      command: '/security',
      description: 'Enable security PR notifications',
    },
    {
      command: '/eku',
      description: 'Enable daily incident digest',
    },
  ];

  const { handleCommonInstall } = useComposioInstaller();

  const handleConnect = async () => {
    if (!organization) return;

    dispatch({ type: 'SET_CONNECTING', payload: true });
    try {
      // Use Composio for Slack installation
      handleCommonInstall(
        'slackbot',
        undefined,
        undefined,
        '/settings?tab=team&page-tab=slack'
      );
      dispatch({ type: 'SET_CONNECTING', payload: false });
    } catch (error) {
      console.error('Error initiating Slack connection:', error);
      dispatch({ type: 'SET_CONNECTING', payload: false });
    }
  };

  const handleConfigureChannel = (
    channelId: string,
    channelName: string,
    notifications: string[]
  ) => {
    const frontendNotifications =
      mapBackendToFrontendNotifications(notifications);
    dispatch({
      type: 'OPEN_CONFIG',
      payload: {
        id: channelId,
        name: channelName,
        notifications: frontendNotifications,
      },
    });
  };

  const handleSaveConfig = async (
    channelId: string,
    notifications: string[]
  ) => {
    if (!organization?.OrgUUID) return;

    try {
      // Find the channel to check if bot is a member
      const channel = data?.channels?.find((c) => c.ChannelId === channelId);
      const isBotMember = channel?.IsBotMember || false;

      // If bot is not a member and user is enabling any features, add the bot first
      if (!isBotMember && notifications.length > 0) {
        const addBotResponse = await addBotToChannel(
          {
            OrgUUID: organization.OrgUUID,
            ChannelId: channelId,
          },
          headerObj
        );

        if (!addBotResponse?.Success) {
          return;
        }
      }

      // Send frontend notification IDs directly to backend
      const response = await updateChannelSubscriptions(
        {
          OrgUUID: organization.OrgUUID,
          ChannelId: channelId,
          Subscriptions: notifications,
        },
        headerObj
      );

      if (response?.Success) {
        // Refetch the channels data
        await channels.refetch();
      } else {
      }
    } catch (error) {}
  };

  const handleRemoveAllNotificationsClick = (
    channelId: string,
    channelName: string
  ) => {
    dispatch({
      type: 'OPEN_REMOVE_NOTIFICATIONS',
      payload: { id: channelId, name: channelName },
    });
  };

  const handleRemoveAllNotificationsConfirm = async () => {
    if (!organization?.OrgUUID || !channelToRemove) return;

    try {
      // Send empty array to remove all notifications
      const response = await updateChannelSubscriptions(
        {
          OrgUUID: organization.OrgUUID,
          ChannelId: channelToRemove.id,
          Subscriptions: [],
        },
        headerObj
      );

      if (response?.Success) {
        // Refetch the channels data
        await channels.refetch();
        dispatch({ type: 'CLOSE_REMOVE_NOTIFICATIONS' });
      } else {
      }
    } catch (error) {}
  };

  const handleDisconnectClick = () => {
    dispatch({ type: 'SET_DISCONNECT_MODAL', payload: true });
  };

  const handleDisconnectConfirm = async () => {
    if (!organization?.OrgUUID) return;

    try {
      const response = await disconnectSlack(
        { OrgUUID: organization.OrgUUID },
        headerObj
      );

      if (response?.Success) {
        // Refetch the data to update UI
        await configured.refetch();
        await channels.refetch();
      } else {
      }
    } catch (error) {}
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="space-y-8">
          {/* Header Skeleton */}
          <div className="animate-pulse">
            <div className="h-8 bg-ds-gray-subtler rounded w-64 mb-2"></div>
            <div className="h-4 bg-ds-gray-subtler rounded w-96"></div>
          </div>

          {/* Workspace Connection Skeleton */}
          <div className="rounded-lg p-4 sm:p-6 border bg-ds-surface-white border-ds-border-subtlest">
            <div className="animate-pulse">
              <div className="h-5 bg-ds-gray-subtler rounded w-40 mb-4"></div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-4 bg-ds-gray-subtler rounded w-20"></div>
                  <div className="h-4 bg-ds-gray-subtler rounded w-32"></div>
                  <div className="h-6 bg-ds-gray-subtler rounded w-20"></div>
                </div>
                <div className="h-8 bg-ds-gray-subtler rounded w-32"></div>
              </div>
            </div>
          </div>

          {/* Connected Channels Skeleton */}
          <div>
            <div className="animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="h-5 bg-ds-gray-subtler rounded w-40 mb-1"></div>
                  <div className="h-4 bg-ds-gray-subtler rounded w-80"></div>
                </div>
                <div className="h-8 bg-ds-gray-subtler rounded w-24"></div>
              </div>

              <div className="rounded-lg border overflow-hidden bg-ds-surface-white border-ds-border-subtlest">
                {/* Table Header Skeleton */}
                <div className="grid grid-cols-[minmax(150px,2fr),minmax(250px,3fr),minmax(120px,1.5fr),minmax(80px,auto)] gap-4 px-6 py-3 border-b border-ds-border-subtlest">
                  <div className="h-4 bg-ds-gray-subtler rounded w-24"></div>
                  <div className="h-4 bg-ds-gray-subtler rounded w-28"></div>
                  <div className="h-4 bg-ds-gray-subtler rounded w-16"></div>
                  <div className="h-4 bg-ds-gray-subtler rounded w-8"></div>
                </div>

                {/* Table Rows Skeleton */}
                <div className="divide-y divide-ds-border-subtlest">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={`skeleton-row-${index}`}
                      className="grid grid-cols-[minmax(150px,2fr),minmax(250px,3fr),minmax(120px,1.5fr),minmax(80px,auto)] gap-4 px-6 py-4 items-center"
                    >
                      <div className="h-4 bg-ds-gray-subtler rounded w-20"></div>
                      <div className="flex gap-2">
                        <div className="h-5 bg-ds-gray-subtler rounded w-16"></div>
                        <div className="h-5 bg-ds-gray-subtler rounded w-20"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 bg-ds-gray-subtler rounded-full"></div>
                        <div className="h-4 bg-ds-gray-subtler rounded w-12"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-8 w-8 bg-ds-gray-subtler rounded"></div>
                        <div className="h-8 w-8 bg-ds-gray-subtler rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Add Bot to New Channel Skeleton */}
          <div>
            <div className="animate-pulse">
              <div className="h-5 bg-ds-gray-subtler rounded w-48 mb-4"></div>
              <div className="rounded-lg border bg-ds-surface-white border-ds-border-subtlest">
                <div className="p-6 flex flex-col lg:flex-row gap-8 lg:gap-12">
                  <div className="flex-1 space-y-4">
                    <div className="h-4 bg-ds-gray-subtler rounded w-80 mb-4"></div>
                    <div className="space-y-4">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div
                          key={`skeleton-bot-${index}`}
                          className="flex items-start gap-4"
                        >
                          <div className="h-6 w-6 bg-ds-gray-subtler rounded-full flex-shrink-0"></div>
                          <div className="h-4 bg-ds-gray-subtler rounded w-64"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="h-5 bg-ds-gray-subtler rounded w-40 mb-4"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div
                          key={`skeleton-channel-${index}`}
                          className="p-3 rounded"
                        >
                          <div className="h-4 bg-ds-gray-subtler rounded w-20 mb-1"></div>
                          <div className="h-3 bg-ds-gray-subtler rounded w-32"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="rounded-lg p-6 border bg-ds-danger-subtlest border-ds-danger-subtler">
          <p className="text-sm text-ds-danger-default">
            {error.message || 'An error occurred while fetching Slack data'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-8 motion-stagger">
        {/* Header */}
        <SettingsHeader
          title="Slack Integration"
          description="Connect Entelligence.AI to your Slack workspace to receive intelligent notifications about your team's performance, security, and insights."
        />

        {/* Workspace Connection */}
        <SlackWorkspaceConnection
          isConfigured={data.isConfigured}
          workspaceName={data.workspaceName}
          workspaceIcon={data.workspaceIcon}
          isConnecting={isConnecting}
          onConnect={handleConnect}
          onDisconnect={handleDisconnectClick}
        />

        {data.isConfigured && (
          <>
            {/* DM Notification Settings */}
            <div className="rounded-lg p-4 sm:p-6 border bg-ds-surface-white border-ds-border-subtlest">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1 text-sm text-ds-text-darker">
                    Notify reviewers via
                  </h3>
                  <div className="relative">
                    <Select
                      value={
                        (dmStatusQuery.data?.DmEnabled ?? false)
                          ? 'dm'
                          : 'channel'
                      }
                      onValueChange={(value) => {
                        if (value != null) handleToggleDmNotifications(value);
                      }}
                      disabled={
                        !isAdmin ||
                        dmStatusQuery.isLoading ||
                        updateDmStatusMutation.isPending ||
                        !data.isConfigured
                      }
                    >
                      <SelectTrigger
                        className={`w-full bg-ds-surface-white border-ds-border-subtlest text-ds-text-darker hover:border-ds-border-subtler ${!isAdmin || dmStatusQuery.isLoading || updateDmStatusMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <SelectValue>
                          {(dmStatusQuery.data?.DmEnabled ?? false)
                            ? 'Slack DM'
                            : 'Slack Channels'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-ds-surface-white border-ds-border-subtlest">
                        <SelectItem
                          value="channel"
                          className="text-ds-text-darker hover:bg-ds-gray-subtlest"
                        >
                          Slack Channels
                        </SelectItem>
                        <SelectItem
                          value="dm"
                          className="text-ds-text-darker hover:bg-ds-gray-subtlest"
                        >
                          Slack DM
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="mt-2 text-xs text-ds-text-subtle">
                    {(dmStatusQuery.data?.DmEnabled ?? false)
                      ? 'Only assigned reviewers. No channel spam.'
                      : 'All notifications will be sent to configured Slack channels.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Check if there are any channels */}
            {data.channels.length > 0 ? (
              /* Connected Channels Table */
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium mb-1 text-sm text-ds-text-darker">
                      Slack Channels
                    </h3>
                    <p className="text-sm text-ds-text-subtle">
                      All channels where the Entelligence.AI bot can be added.
                      Configure notifications for each channel.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        router.push('/settings?tab=team&page-tab=slack');
                      }}
                    >
                      Map Users
                    </Button>
                    <Button
                      onClick={() => {
                        // Open config modal with first available channel or current channel
                        const firstChannel = data?.channels?.[0];
                        if (firstChannel) {
                          const frontendNotifications =
                            mapBackendToFrontendNotifications(
                              firstChannel.Subscriptions
                            );
                          dispatch({
                            type: 'OPEN_CONFIG',
                            payload: {
                              id: firstChannel.ChannelId,
                              name: firstChannel.ChannelName,
                              notifications: frontendNotifications,
                            },
                          });
                        }
                      }}
                    >
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <span className="hidden sm:inline">Add Channel</span>
                      <span className="sm:hidden">Add</span>
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg border overflow-hidden bg-ds-surface-white border-ds-border-subtlest">
                  {/* Scrollable Table Container */}
                  <div className="overflow-x-auto">
                    {/* Table Header */}
                    <div
                      className="grid grid-cols-[minmax(150px,2fr),minmax(250px,3fr),minmax(120px,1.5fr),minmax(80px,auto)] gap-4 px-6 py-3 border-b border-ds-border-subtlest text-xs font-medium text-ds-text-subtle"
                      style={{ minWidth: '700px' }}
                    >
                      <div>Slack Channel</div>
                      <div>Notifications</div>
                      <div>Status</div>
                      <div> </div>
                    </div>

                    {/* Table Rows */}
                    <div
                      className="divide-y divide-ds-border-subtlest"
                      style={{ minWidth: '700px' }}
                    >
                      {data.channels
                        .sort((a, b) => {
                          // First sort by activity status (active channels first)
                          const aIsActive =
                            a.Subscriptions && a.Subscriptions.length > 0;
                          const bIsActive =
                            b.Subscriptions && b.Subscriptions.length > 0;

                          if (aIsActive && !bIsActive) return -1;
                          if (!aIsActive && bIsActive) return 1;

                          // Then sort alphabetically by channel name
                          return a.ChannelName.localeCompare(b.ChannelName);
                        })
                        .map((channel) => (
                          <div
                            key={channel.ChannelId}
                            className="grid grid-cols-[minmax(150px,2fr),minmax(250px,3fr),minmax(120px,1.5fr),minmax(80px,auto)] gap-4 px-6 py-4 items-center"
                          >
                            <div
                              className="flex items-center gap-1 text-sm text-ds-text-darker truncate"
                              title={`${channel.IsPrivate ? '🔒 ' : '#'}${channel.ChannelName}`}
                            >
                              {channel.IsPrivate ? (
                                <LockSimpleIcon
                                  size={14}
                                  weight="fill"
                                  className="shrink-0"
                                />
                              ) : (
                                <span aria-hidden>#</span>
                              )}
                              <span className="truncate">
                                {channel.ChannelName}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 min-w-0">
                              {channel.Subscriptions &&
                              channel.Subscriptions.length > 0 ? (
                                channel.Subscriptions.map((notification) => (
                                  <span
                                    key={notification}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ds-gray-subtlest text-ds-text-darker border border-ds-border-subtlest"
                                  >
                                    {getNotificationName(notification)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-ds-text-subtler">
                                  No active notifications
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  channel.Subscriptions &&
                                  channel.Subscriptions.length > 0
                                    ? 'bg-ds-success-default'
                                    : 'bg-ds-text-subtle'
                                }`}
                              ></span>
                              <span
                                className={`text-sm ${
                                  channel.Subscriptions &&
                                  channel.Subscriptions.length > 0
                                    ? 'text-ds-success-default font-semibold'
                                    : 'text-ds-text-subtle'
                                }`}
                              >
                                {channel.Subscriptions &&
                                channel.Subscriptions.length > 0
                                  ? 'Active'
                                  : 'Inactive'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleConfigureChannel(
                                    channel.ChannelId,
                                    channel.ChannelName,
                                    channel.Subscriptions
                                  )
                                }
                                title={
                                  channel.IsBotMember
                                    ? 'Configure notifications'
                                    : 'Configure notifications (bot will be added automatically)'
                                }
                              >
                                <svg
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleRemoveAllNotificationsClick(
                                    channel.ChannelId,
                                    channel.ChannelName
                                  )
                                }
                                disabled={
                                  !channel.Subscriptions ||
                                  channel.Subscriptions.length === 0
                                }
                                title={
                                  !channel.Subscriptions ||
                                  channel.Subscriptions.length === 0
                                    ? 'No active notifications to remove'
                                    : 'Remove all notifications'
                                }
                              >
                                <svg
                                  className="text-ds-danger-default"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Empty State - No Channels Available */
              <div className="rounded-lg border bg-ds-gray-subtlest border-ds-border-subtlest">
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <h3 className="text-base font-medium mb-2 text-ds-text-darker">
                    No Channels Available
                  </h3>
                  <p className="text-sm mb-6 text-ds-text-subtle">
                    No channels are available for configuration. Make sure the
                    bot has access to channels in your workspace.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Bot to New Channel */}
        <div>
          <h3 className="font-medium mb-4 text-sm text-ds-text-darker">
            Add Bot to New Channel
          </h3>
          <div className="rounded-lg border bg-ds-surface-white border-ds-border-subtlest">
            <div className="p-6 flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-1 space-y-4 min-w-0">
                <p className="text-sm mb-4 text-ds-text-darker">
                  To add Entelligence.AI to a new Slack channel:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ds-brand-subtlest ds-text-body-xs-semibold text-ds-brand-subtle">
                      1
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-ds-text-darker">
                        Go to your Slack channel
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ds-brand-subtlest ds-text-body-xs-semibold text-ds-brand-subtle">
                      2
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-ds-text-darker">
                        Type:{' '}
                        <code className="rounded-md bg-ds-gray-subtlest px-2 py-1 ds-text-code-sm text-ds-text-darker">
                          @Entelligence.AI
                        </code>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ds-brand-subtlest ds-text-body-xs-semibold text-ds-brand-subtle">
                      3
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-ds-text-darker">
                        The bot will join and guide you through setup
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-ds-gray-subtlest p-3">
                  <InfoIcon className="mt-0.5 size-4 shrink-0 text-ds-text-subtle" />
                  <p className="ds-text-body-xs-medium text-ds-text-subtle">
                    Once added, the channel will automatically appear in the
                    &quot;Connected Channels&quot; list above.
                  </p>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-4 text-ds-text-darker">
                  Available Commands
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {commands.map((cmd) => (
                    <div
                      key={cmd.command}
                      className="rounded-lg border border-ds-border-subtler bg-ds-gray-subtlest p-3"
                    >
                      <code className="mb-1 block ds-text-code-md text-ds-text-darker">
                        {cmd.command}
                      </code>
                      <p className="ds-text-body-xs-medium text-ds-text-subtle">
                        {cmd.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Modal — only mount when open so state initializes from props */}
      {configModalOpen && selectedChannel && (
        <SlackChannelConfigModal
          key={selectedChannel.id}
          isOpen={true}
          onClose={() => dispatch({ type: 'CLOSE_CONFIG' })}
          channelId={selectedChannel.id}
          channelName={selectedChannel.name}
          currentSubscriptions={selectedChannel.notifications}
          isBotMember={
            data?.channels?.find((c) => c.ChannelId === selectedChannel.id)
              ?.IsBotMember || false
          }
          availableChannels={data?.channels || []}
          onSave={handleSaveConfig}
          onAddChannel={() =>
            dispatch({ type: 'SET_ADD_CHANNEL_MODAL', payload: true })
          }
        />
      )}

      {/* Add Channel Modal */}
      <SlackAddChannelModal
        isOpen={addChannelModalOpen}
        onClose={() =>
          dispatch({ type: 'SET_ADD_CHANNEL_MODAL', payload: false })
        }
      />

      {/* Disconnect Confirmation Modal */}
      <ConfirmationModal
        isOpen={disconnectModalOpen}
        onClose={() =>
          dispatch({ type: 'SET_DISCONNECT_MODAL', payload: false })
        }
        onConfirm={handleDisconnectConfirm}
        title="Disconnect Slack?"
        message="Are you sure you want to disconnect Slack? This will remove all channel configurations and subscriptions."
        confirmText="Disconnect"
        cancelText="Cancel"
        isDanger={true}
      />

      {/* Remove Notifications Confirmation Modal */}
      <ConfirmationModal
        isOpen={removeNotificationsModalOpen}
        onClose={() => dispatch({ type: 'CLOSE_REMOVE_NOTIFICATIONS' })}
        onConfirm={handleRemoveAllNotificationsConfirm}
        title="Remove All Notifications?"
        message={`Are you sure you want to remove all notifications from #${channelToRemove?.name}? This will disable all active notifications for this channel.`}
        confirmText="Remove All"
        cancelText="Cancel"
        isDanger={true}
      />
    </div>
  );
}
